import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Search, Mail, Phone, MapPin, Ruler, CreditCard, ShoppingBag, Trash2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/customers');
      setCustomers(data.customers || []);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => customers.filter((c) => {
    if (filter === 'unpaid' && !(c.payment && c.payment.status === 'pending')) return false;
    if (filter === 'paid' && !(c.payment && c.payment.status === 'paid')) return false;
    if (filter === 'no-payment' && c.payment) return false;
    if (filter === 'founders' && c.user_role !== 'founder') return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [c.name, c.email, c.phone, c.founder_number].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  }), [customers, filter, q]);

  const counts = {
    all: customers.length,
    unpaid: customers.filter((c) => c.payment && c.payment.status === 'pending').length,
    paid: customers.filter((c) => c.payment && c.payment.status === 'paid').length,
    'no-payment': customers.filter((c) => !c.payment).length,
    founders: customers.filter((c) => c.user_role === 'founder').length,
  };

  const removeCustomer = async (c) => {
    if (!c.application?.id) { toast.info('No application record to delete for this account.'); return; }
    if (!confirm(`Delete ${c.name || c.email} and all their data (application, payment, orders)? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/applications/${c.application.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-customers">
      <div className="h-panel-thin p-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="h-eyebrow">Customer records</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Customers</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>
            Everyone in the Heritage database. Shoppers, members, and migrated founder applicants.
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm('Convert every founder applicant on record into a Heritage member? This is safe to run multiple times.')) return;
            try {
              const { data } = await api.post('/admin/founders/migrate-to-customers');
              toast.success(`${data.created} new · ${data.updated} updated`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || 'Migration failed'); }
          }}
          className="h-btn-outline text-xs"
          data-testid="migrate-founders"
        >
          Migrate founder applicants → customers
        </button>
      </div>

      <Panel className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {[
              ['all', 'All'], ['founders', 'Founders'], ['unpaid', 'Unpaid'], ['paid', 'Paid'], ['no-payment', 'No invoice'],
            ].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                className="rounded-full px-3 py-1 text-xs h-serif"
                style={{
                  background: filter === k ? 'var(--heritage-plum)' : 'transparent',
                  color: filter === k ? 'var(--heritage-cream)' : 'var(--heritage-plum)',
                  border: '1px solid var(--heritage-plum)',
                }}
                data-testid={`customers-filter-${k}`}
              >
                {label} <span className="opacity-70">({counts[k] || 0})</span>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / phone / founder #"
              className="pl-8 pr-3 py-1.5 rounded-full text-sm"
              style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)', minWidth: 280 }}
              data-testid="customers-search" />
          </div>
        </div>
      </Panel>

      {loading && <div className="h-eyebrow">Loading...</div>}

      {!loading && filtered.length === 0 && (
        <Panel className="p-10 text-center">
          <div className="h-eyebrow">No customers</div>
          <div className="h-serif text-xl mt-2" style={{ color: 'var(--heritage-plum)' }}>Nothing to show for this filter.</div>
        </Panel>
      )}

      <div className="grid gap-3">
        {filtered.map((c) => {
          const key = c.user_id || c.application?.id || c.email;
          const isOpen = !!expanded[key];
          const payStatus = c.payment?.status;
          const statusPill = c.user_role === 'founder' ? { label: 'Founder', bg: 'rgba(75,24,56,0.15)' }
            : payStatus === 'paid' ? { label: 'Paid', bg: 'rgba(91,110,44,0.15)' }
            : payStatus === 'pending' ? { label: 'Awaiting payment', bg: 'rgba(75,24,56,0.08)' }
            : !c.payment ? { label: 'No invoice yet', bg: 'rgba(154,110,44,0.15)' }
            : { label: payStatus || 'Member', bg: 'rgba(75,24,56,0.08)' };
          return (
            <Panel key={key} className="p-4" data-testid={`customer-row-${c.email}`}>
              <div className="flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="h-display text-xl" style={{ color: 'var(--heritage-plum)' }}>{c.name || '(no name)'}</span>
                    {c.founder_number && <span className="h-eyebrow text-[10px] opacity-80">{c.founder_number}</span>}
                    <span className="rounded-full px-2 py-0.5 text-[10px] h-serif uppercase tracking-wider"
                          style={{ background: statusPill.bg, color: 'var(--heritage-plum)' }}>
                      {statusPill.label}
                    </span>
                    {c.temp_password && <span className="text-[10px] opacity-70 h-eyebrow" style={{ color: 'var(--heritage-plum)' }}>Needs to set password</span>}
                  </div>
                  <div className="mt-1 text-sm flex flex-wrap gap-x-4 gap-y-1" style={{ color: 'var(--heritage-plum)' }}>
                    <span className="flex items-center gap-1 opacity-90"><Mail size={12} /> {c.email}</span>
                    {c.phone && <span className="flex items-center gap-1 opacity-90"><Phone size={12} /> {c.phone}</span>}
                    {c.clothing_size && <span className="flex items-center gap-1 opacity-90"><Ruler size={12} /> {c.clothing_size}</span>}
                  </div>
                  {c.address && <div className="mt-1 text-xs opacity-75 flex items-start gap-1" style={{ color: 'var(--heritage-plum)' }}><MapPin size={12} className="mt-0.5" /> {c.address}</div>}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--heritage-plum)' }}>
                    <span className="flex items-center gap-1 opacity-90">
                      <CreditCard size={12} /> {c.payment ? `${c.payment.reference} · ${c.payment.plan_type} · ${fmt(c.payment.amount)}` : 'no invoice yet'}
                    </span>
                    <span className="flex items-center gap-1 opacity-90">
                      <ShoppingBag size={12} /> {c.orders_count} add-on order{c.orders_count === 1 ? '' : 's'} · {fmt(c.orders_total)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    <button onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))} className="h-btn-outline text-xs">
                      {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                    <button onClick={() => removeCustomer(c)}
                      className="h-btn-outline text-xs"
                      style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }}
                      data-testid={`customer-delete-${c.email}`}
                      title="Delete customer + all their data">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="mt-4 pt-4 border-t grid md:grid-cols-2 gap-4 text-sm" style={{ borderColor: 'rgba(75,24,56,0.15)', color: 'var(--heritage-plum)' }}>
                  <div>
                    <div className="h-eyebrow text-[10px]">Application</div>
                    {c.application ? (
                      <ul className="mt-1 space-y-1">
                        <li>Status: <span className="h-serif">{c.application.admin_status || c.application.status}</span></li>
                        <li>Tier: <span className="h-serif">{c.application.tier_name || 'not chosen'}</span></li>
                        {c.application.reason && <li className="italic opacity-80">"{c.application.reason}"</li>}
                      </ul>
                    ) : <div className="opacity-70">No application on record.</div>}
                  </div>
                  <div>
                    <div className="h-eyebrow text-[10px]">Payment</div>
                    {c.payment ? (
                      <ul className="mt-1 space-y-1">
                        <li>Reference: <span className="h-serif">{c.payment.reference}</span></li>
                        <li>Plan: <span className="h-serif">{c.payment.plan_type}</span></li>
                        <li>Status: <span className="h-serif">{c.payment.status}</span></li>
                        <li>Instalments: {c.payment.paid_instalments || 0}/{c.payment.total_instalments || 1}</li>
                        <li className="pt-1">
                          <a href="/admin/payments" className="opacity-80 hover:opacity-100 inline-flex items-center gap-1"><ExternalLink size={11} /> Open in Payments</a>
                        </li>
                      </ul>
                    ) : (
                      <div className="opacity-80">
                        No invoice. Head to <a href="/admin/applications" className="underline">Applications</a> and use "Create invoice" to bill this customer.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
